import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetSponsorUserDetails,
  useUpdateSponsorUser,
  useDeleteSponsorUser,
} from "../../../custom-hooks/queries";
import NotificationModal from "../../../components/NotificationModal";
import Modal from "../../../components/Modal";
import { v4 as uuidv4 } from "uuid";
import {
  rightsCategories,
  rightsDescriptions,
  rightsList,
  roleDefinitions,
  RIGHTS_DISPLAY_NAMES,
  ROLE_DISPLAY_NAMES,
  PERMISSION_CATEGORY_DISPLAY_NAMES,
} from "../../../utils/constants";
import FormSkeletonLoader from "../../../components/FormSkeletonLoader";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Required"),
  first_name: yup.string().required("Required"),
  last_name: yup.string().required("Required"),
  office_phone_number: yup.string(),
  phone_number: yup.string(),
  contact_preference: yup.string().required("Required"),
  role: yup.array().min(1, "At least one role must be selected"),
});

// Role definitions with descriptions and default rights

// Update the rights categories to match AddNewSponsorForm

// Complete rights list

// Add the rights descriptions object (same as in AddNewSponsorForm)

const ManageSponsor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: sponsorDetails, isLoading } = useGetSponsorUserDetails(id);
  const { mutate: updateSponsor, isError } = useUpdateSponsorUser();
  const {
    mutate: deleteUser,
    isLoading: isDeleting,
    isError: deleteError,
  } = useDeleteSponsorUser();

  // Keep the selectedRights state for tracking rights
  const [selectedRights, setSelectedRights] = useState({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      contact_preference: "Mobile_Phone",
      role: [],
    },
  });

  // Add this near the top of the component to prevent overriding existing permissions
  const hasLoadedInitialData = React.useRef(false);
  // Track if role change is from user action (vs initial data load)
  const userChangedRoles = React.useRef(false);

  // Update the existing useEffect for loading initial data
  useEffect(() => {
    if (sponsorDetails) {
      setValue("email", sponsorDetails.email);
      setValue("first_name", sponsorDetails.first_name);
      setValue("last_name", sponsorDetails.last_name);
      setValue("phone_number", sponsorDetails.phone_number);
      setValue("office_phone_number", sponsorDetails.office_phone_number);
      setValue("contact_preference", sponsorDetails.contact_preference);
      setValue("sponsor_name", sponsorDetails.sponsor_name || "");

      // Extract user rights from the backend data
      const userRights = sponsorDetails.sponsor_rights_dto
        ? sponsorDetails.sponsor_rights_dto.map((right) => right.rights)
        : [];

      // Reset all rights checkboxes first
      rightsList.forEach((right) => {
        setValue(right, false);
      });

      // Then check the rights the user has
      userRights.forEach((right) => {
        setValue(right, true);
      });
      // Chat Diligent access is stored separately on the user, not in sponsor_rights_dto
      setValue(
        "Access to Chat Diligent",
        sponsorDetails.chat_diligent_access === true
      );

      // Ensure My Vendors is selected by default for RFI Manager and Sponsor Admin
      const userRoles =
        sponsorDetails?.sponsor_subrole_dto?.map((role) => role.sub_role) || [];
      if (
        userRoles.includes("RFI Manager") ||
        userRoles.includes("Sponsor Admin")
      ) {
        setValue("My Vendors", true, { shouldDirty: true });
      }

      // Set roles from sponsor_subrole_dto
      if (
        sponsorDetails.sponsor_subrole_dto &&
        sponsorDetails.sponsor_subrole_dto.length > 0
      ) {
        const roles = sponsorDetails.sponsor_subrole_dto.map(
          (role) => role.sub_role
        );
        setValue("role", roles);
      }

      // Mark that we've loaded initial data
      hasLoadedInitialData.current = true;
    }
  }, [sponsorDetails, setValue]);

  const selectedRoles = watch("role") || [];

  // Update the useEffect hook for permission changes
  useEffect(() => {
    // Only apply role-based permissions when user manually changes roles
    // Skip if this is from initial data load (to preserve existing permissions from API)
    if (!userChangedRoles.current) {
      return;
    }

    // Reset the flag
    userChangedRoles.current = false;

    // Step 1: Clear all existing permissions first
    rightsList.forEach((right) => {
      setValue(right, false, { shouldDirty: true });
    });

    // Step 2: If we have any roles selected, apply appropriate permissions
    if (selectedRoles.length > 0) {
      // Always set general permissions first (except Trial Experience Dashboard and Access to Chat Diligent - off by default)
      rightsCategories["General Permissions"].rights.forEach((right) => {
        if (right !== "Trial Experience Dashboard" && right !== "Access to Chat Diligent") {
          setValue(right, true, { shouldDirty: true });
        }
      });

      // Then apply role-specific permissions
      selectedRoles.forEach((role) => {
        if (role === "Sponsor Admin") {
          // Admin gets all permissions (except Trial Experience Dashboard and Access to Chat Diligent)
          rightsList.forEach((right) => {
            if (right !== "Trial Experience Dashboard" && right !== "Access to Chat Diligent") {
              setValue(right, true, { shouldDirty: true });
            }
          });
        } else if (role === "RFI Manager") {
          // Set RFI permissions
          rightsCategories["RFI Permissions"].rights.forEach((right) => {
            setValue(right, true, { shouldDirty: true });
          });
        } else if (role === "VQA Manager") {
          // Set Assessment permissions
          rightsCategories["Assessment Permissions"].rights.forEach((right) => {
            setValue(right, true, { shouldDirty: true });
          });
        }
      });
    }
  }, [selectedRoles, setValue]);

  // Handle role selection with improved functionality
  const handleRoleChange = (e, role) => {
    const isChecked = e.target.checked;

    // Get current roles
    const currentRoles = getValues("role") || [];
    let newRoles = [];

    if (isChecked) {
      // If selecting Sponsor Admin, only allow it
      if (role === "Sponsor Admin") {
        newRoles = ["Sponsor Admin"];
      }
      // If selecting another role when Sponsor Admin is selected, switch to just this role
      else if (currentRoles.includes("Sponsor Admin")) {
        newRoles = [role];
      }
      // Otherwise add this role to existing selections
      else {
        newRoles = [...currentRoles, role];
      }
    } else {
      // If unchecking a role, just remove it
      newRoles = currentRoles.filter((r) => r !== role);
    }

    // Mark that user manually changed roles (so useEffect will apply permissions)
    userChangedRoles.current = true;

    // Update the form with new role selections
    setValue("role", newRoles);
  };

  // Function to check if a role should be disabled
  const isRoleDisabled = (role) => {
    const currentRoles = getValues("role") || [];
    if (role === "Sponsor Admin") {
      // Disable Sponsor Admin if any other role is selected
      return currentRoles.some((r) => r !== "Sponsor Admin" && r !== role);
    } else {
      // Disable other roles if Sponsor Admin is selected
      return currentRoles.includes("Sponsor Admin");
    }
  };

  const onSubmit = (data) => {
    // Get only the rights that are checked (ChatDiligentAccess from "Access to Chat Diligent" in sponsor_rights_dto)
    const rights = rightsList.filter((right) => data[right] === true);

    // Prepare roles as array
    const rolesArray = Array.isArray(selectedRoles)
      ? selectedRoles
      : [selectedRoles];

    // Create a map of existing subroles with their IDs for quick lookup
    const existingSubroleMap = {};
    if (
      sponsorDetails?.sponsor_subrole_dto &&
      Array.isArray(sponsorDetails.sponsor_subrole_dto)
    ) {
      sponsorDetails.sponsor_subrole_dto.forEach((role) => {
        if (role && role.sub_role && role.sponsor_subrole_id) {
          existingSubroleMap[role.sub_role] = role.sponsor_subrole_id;
        }
      });
    }

    // Create a map of existing rights with their IDs for quick lookup
    const existingRightsMap = {};
    if (
      sponsorDetails?.sponsor_rights_dto &&
      Array.isArray(sponsorDetails.sponsor_rights_dto)
    ) {
      sponsorDetails.sponsor_rights_dto.forEach((right) => {
        if (right && right.rights && right.sponsor_rights_id) {
          existingRightsMap[right.rights] = right.sponsor_rights_id;
        }
      });
    }

    // Log the maps to verify we're capturing IDs correctly

    // Build sponsor_subrole_dto using existing IDs where possible
    const sponsor_subrole_dto = rolesArray.map((role) => {
      const existingId = existingSubroleMap[role];

      // Log if we're missing an ID we expected to find
      if (!existingId && Object.keys(existingSubroleMap).length > 0) {
        console.warn(`Missing ID for role: ${role}`);
      }

      return {
        sponsor_subrole_id: existingId || uuidv4(), // Generate UUID instead of null
        sub_role: role,
      };
    });

    // Build sponsor_rights_dto using existing IDs where possible
    const sponsor_rights_dto = rights.map((right) => {
      const existingId = existingRightsMap[right];

      // Log if we're missing an ID we expected to find
      if (!existingId && Object.keys(existingRightsMap).length > 0) {
        console.warn(`Missing ID for right: ${right}`);
      }

      return {
        sponsor_rights_id: existingId || uuidv4(), // Generate UUID instead of null
        rights: right,
      };
    });

    // Build the sponsor data
    const sponsorData = {
      email: data.email,
      phone_number: data.phone_number,
      office_phone_number: data.office_phone_number,
      contact_preference: data.contact_preference,
      first_name: data.first_name,
      last_name: data.last_name,
      profile_pic_url: sponsorDetails?.profile_pic_url || "",
      sponsor_subrole_dto,
      sponsor_rights_dto,
      sponsor_id: sponsorDetails.sponsor_id,
    };

    updateSponsor(
      { id, userData: sponsorData },
      {
        onSuccess: () => {
          setMessage("Sponsor updated successfully");
          setShow(true);
          // setTimeout(
          //   () =>
          //     navigate(
          //       `/Backend/Users/Sponsors/list/${sponsorDetails.sponsor_id}`
          //     ),
          //   2000
          // );
        },
        onError: (error) => {
          setMessage(
            error.response?.data?.message || "Failed to update sponsor"
          );
          setShow(true);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteUser(id, {
      onSuccess: () => {
        setMessage("User deleted successfully");
        setShow(true);
        setDeleteModalOpen(false);
        setTimeout(
          () =>
            navigate(
              `/Backend/Users/Sponsors/list/${sponsorDetails.sponsor_id}`
            ),
          2000
        );
      },
      onError: (error) => {
        setMessage(error.response?.data?.message || "Failed to delete user");
        setShow(true);
        setDeleteModalOpen(false);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-[60vh]">
        <div className="animate-pulse text-center">
          <p className="text-gray-500">
            <FormSkeletonLoader />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-5">
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
        <h2 className="text-lg font-semibold">Manage Sponsor</h2>
        <p className="text-sm text-gray-600">
          Update the sponsor information below.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        {/* Email */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Email
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("email")}
              type="email"
              className="ring-0 border-gray-300 bg-gray-100 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] cursor-not-allowed"
              disabled={true}
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Email address cannot be changed
            </p>
          </div>
        </div>

        {/* Roles section - simplified without expansion */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Roles
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <fieldset className="rounded-md bg-white w-[300px] lg:w-[400px] xl:w-[500px]">
              {Object.keys(roleDefinitions).map((role) => (
                <div key={role} className="">
                  <div
                    className={`flex items-center p-4 ${
                      isRoleDisabled(role) ? "opacity-50 " : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`role-${role}`}
                      value={role}
                      checked={selectedRoles.includes(role)}
                      onChange={(e) => handleRoleChange(e, role)}
                      disabled={isRoleDisabled(role)}
                      className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                        isRoleDisabled(role)
                          ? "cursor-normal "
                          : "cursor-pointer"
                      } `}
                    />
                    <label htmlFor={`role-${role}`} className="ml-3 flex-1">
                      <span className="block text-sm font-medium text-gray-900">
                        {ROLE_DISPLAY_NAMES[role] || role}
                      </span>
                      <span className="block text-sm text-gray-500 mt-1">
                        {roleDefinitions[role].description}
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </fieldset>
            {errors.role && (
              <p className="mt-2 text-sm text-red-600">{errors.role.message}</p>
            )}

            {/* Hidden inputs to ensure role is registered with React Hook Form */}
            <input type="hidden" {...register("role")} />
          </div>
        </div>

        {/* Keep the Permissions section as is */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Permissions
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <div className="space-y-6 w-[300px] lg:w-[400px] xl:w-[500px]">
              {Object.entries(rightsCategories).map(
                ([category, categoryData]) => (
                  <div key={category} className="rounded-md">
                    <div className="px-4 py-3">
                      <h3 className="text-sm font-medium text-gray-900">
                        {PERMISSION_CATEGORY_DISPLAY_NAMES[category] || category}
                      </h3>
                    </div>
                    <div className="px-4 py-3">
                      <div className="space-y-4">
                        {categoryData.rights.map((rightItem) => {
                          // Get the current value from the form
                          const currentValue = watch(rightItem) || false;

                          // Determine if this right belongs to any of the selected roles
                          const isRoleSpecificRight = selectedRoles.some(
                            (role) => {
                              if (role === "Sponsor Admin") return true; // Admin can modify all rights

                              // For other roles, check if this right belongs to their category
                              if (
                                role === "RFI Manager" &&
                                category === "RFI Permissions"
                              )
                                return true;
                              if (
                                role === "VQA Manager" &&
                                category === "Assessment Permissions"
                              )
                                return true;

                              return false;
                            }
                          );

                          // General permissions are always editable if any role is selected
                          const isGeneralPermission =
                            category === "General Permissions" &&
                            selectedRoles.length > 0;

                          // Admin permissions are only editable by Sponsor Admin
                          const isAdminPermission =
                            category === "Admin Permissions" &&
                            selectedRoles.includes("Sponsor Admin");

                          // Determine if this checkbox should be editable
                          const isEditable =
                            isRoleSpecificRight ||
                            isGeneralPermission ||
                            isAdminPermission;

                          return (
                            <div key={rightItem} className="flex items-start">
                              <div className="flex items-center h-5 pt-1">
                                <input
                                  type="checkbox"
                                  id={`right-${rightItem}`}
                                  name={rightItem}
                                  checked={currentValue}
                                  disabled={!isEditable}
                                  onChange={(e) => {
                                    // Important: Only update this specific right's value
                                    setValue(rightItem, e.target.checked, {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    });
                                  }}
                                  className={`h-4 w-4 rounded border-gray-300 ${
                                    isEditable
                                      ? "text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                      : "text-gray-300 cursor-not-allowed"
                                  }`}
                                />
                              </div>
                              <div
                                className={`ml-3 text-sm ${
                                  !isEditable ? "opacity-50" : ""
                                }`}
                              >
                                <label
                                  htmlFor={`right-${rightItem}`}
                                  className={`font-medium ${
                                    isEditable
                                      ? "text-gray-700"
                                      : "text-gray-400"
                                  }`}
                                >
                                  {RIGHTS_DISPLAY_NAMES[rightItem] || rightItem}
                                </label>
                                <p className="text-xs text-gray-500 mt-1">
                                  {rightsDescriptions[rightItem] ||
                                    "No description available"}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Hidden inputs for all rights */}
            {rightsList.map((right) => (
              <input
                type="hidden"
                key={`hidden-${right}`}
                {...register(right)}
              />
            ))}
          </div>
        </div>

        {/* First Name */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            First Name
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("first_name")}
              type="text"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.first_name && (
              <p className="mt-2 text-sm text-red-600">
                {errors.first_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Last Name */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Last Name
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("last_name")}
              type="text"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.last_name && (
              <p className="mt-2 text-sm text-red-600">
                {errors.last_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Office Phone Number */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Office Phone Number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("office_phone_number")}
              type="tel"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.office_phone_number && (
              <p className="mt-2 text-sm text-red-600">
                {errors.office_phone_number.message}
              </p>
            )}
          </div>
        </div>

        {/* Phone Number */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Phone Number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("phone_number")}
              type="tel"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
          </div>
        </div>

        {/* Contact Preference */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Contact Preference
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <select
              {...register("contact_preference")}
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] [&>option]:bg-white [&>option:hover]:bg-indigo-600 [&>option:hover]:text-white"
            >
              <option value="Mobile_Phone">Mobile Phone</option>
              <option value="Office_Phone">Office Phone</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
          <button
            type="button"
            onClick={() => setDeleteModalOpen(true)}
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-red-600 hover:bg-red-500"
          >
            Delete User
          </button>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/Backend/Users/Sponsors/list/${sponsorDetails.sponsor_id}`
              )
            }
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Update
          </button>
        </div>
      </form>

      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={isError || deleteError ? "Error" : "Success"}
        error={isError || deleteError}
      />

      <Modal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete User"
        description="Are you sure you want to delete this user? This action cannot be undone."
        textBtn="Delete"
        onClick={handleDelete}
        loading={isDeleting}
        color="bg-red-600"
        hoverColor="hover:bg-red-700"
      />
    </div>
  );
};

export default ManageSponsor;
